import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import { resolvePartnerLogoUrl } from "../../lib/utils/partnerAssets";

const EMPTY_FORM = {
  name: "",
  partner_type: "corporate",
  category: "Government",
  status: "active",
  logo: null,
  remove_logo: false,
};

const TYPE_OPTIONS = [
  { value: "corporate", label: "Corporate & Institutional Network" },
  { value: "insurance", label: "Insurance Company" },
  { value: "tpa", label: "TPA Partner" },
];

const PartnerFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const isEditMode = mode === "edit";
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");

  const pageTitle = useMemo(
    () => (isEditMode ? "Edit Partner" : "Add Partner"),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchPartner = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/partners/${id}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Partner not found");
          navigate("/partners");
          return;
        }

        setForm({
          name: item.name || "",
          partner_type: item.partner_type || "corporate",
          category: item.category || "",
          status: item.status || "active",
          logo: null,
          remove_logo: false,
        });
        setLogoPreviewUrl(resolvePartnerLogoUrl(item.logo_url));
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load partner");
        navigate("/partners");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchPartner();
  }, [id, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const handleTypeChange = (value) => {
    setForm((current) => ({
      ...current,
      partner_type: value,
      category: value === "corporate" ? current.category : "",
    }));
    setFormErrors((current) => ({ ...current, partner_type: "", category: "" }));
  };

  const handleLogoChange = (file) => {
    setForm((current) => ({
      ...current,
      logo: file,
      remove_logo: file ? false : current.remove_logo,
    }));
    setFormErrors((current) => ({ ...current, logo: "" }));
  };

  const handleRemoveExistingLogo = () => {
    setLogoPreviewUrl("");
    setForm((current) => ({ ...current, remove_logo: true }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Partner name is required.";
    if (!TYPE_OPTIONS.some((option) => option.value === form.partner_type)) {
      nextErrors.partner_type = "Select a valid partner group.";
    }
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("partner_type", form.partner_type);
      payload.append("category", form.partner_type === "corporate" ? form.category.trim() : "");
      payload.append("status", form.status);
      if (form.logo) payload.append("logo", form.logo);
      if (form.remove_logo) payload.append("remove_logo", "true");

      if (isEditMode) {
        await apiClient.put(`/partners/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Partner updated successfully");
      } else {
        await apiClient.post("/partners", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Partner created successfully");
      }

      navigate("/partners");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save partner");
    } finally {
      setIsSaving(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" /> Loading partner editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/partners")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Partners
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
        <p className="mt-1 text-sm font-medium text-slate-500">Changes to active records appear on the public Corporate Tie-ups page.</p>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Partner Name</label>
              <Input
                value={form.name}
                maxLength={500}
                onChange={(event) => handleInputChange("name", event.target.value)}
                className={`rounded-lg ${formErrors.name ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                placeholder="Enter company or organisation name"
              />
              <FieldError>{formErrors.name}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Partner Group</label>
              <Select value={form.partner_type} onValueChange={handleTypeChange}>
                <SelectTrigger className={formErrors.partner_type ? "border-red-300 focus:ring-red-500/20" : ""}>
                  <SelectValue placeholder="Select partner group" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError>{formErrors.partner_type}</FieldError>
            </div>

            {form.partner_type === "corporate" && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Corporate Category</label>
                <Input
                  value={form.category}
                  maxLength={100}
                  list="partner-category-options"
                  onChange={(event) => handleInputChange("category", event.target.value)}
                  className="rounded-lg"
                  placeholder="Government, Banking, Infrastructure..."
                />
                <datalist id="partner-category-options">
                  <option value="Government" />
                  <option value="Banking" />
                  <option value="Infrastructure" />
                  <option value="Industry" />
                  <option value="Healthcare" />
                </datalist>
                <p className="text-xs text-slate-500">Choose a suggestion or enter a new category.</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Status</label>
              <Select value={form.status} onValueChange={(value) => handleInputChange("status", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active — show on website</SelectItem>
                  <SelectItem value="inactive">Inactive — hide from website</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/70 p-5">
            <InlineFormFileUpload
              label="Partner Logo"
              optionalLabel="(optional)"
              value={form.logo}
              onChange={handleLogoChange}
              existingFiles={logoPreviewUrl ? [logoPreviewUrl] : []}
              onRemoveExisting={handleRemoveExistingLogo}
              accept={IMAGE_TYPES.filter((type) => type !== "image/gif")}
              maxSizeBytes={5 * 1024 * 1024}
              error={formErrors.logo}
              helperText="JPG, PNG or WEBP — maximum 5 MB. A fallback icon is shown when no logo is supplied."
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/partners")}>Cancel</Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Partner" : "Create Partner"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PartnerFormPage;
